<template>
  <v-layout column>
    <v-flex class="pb-16">
      <text-field
        v-model.trim="form.name"
        autofocus
        :validator="vs.form.name"
        label="Workshop name"
      />
    </v-flex>
    <v-layout class="pb-16">
      <v-flex class="pr-30" xs6 md12>
        <datetime-field
          v-model="form.from"
          :min="event.from.toString()"
          :max="form.to ? form.to.toString() : event.to.toString()"
          :error-messages="{
            minDate: 'Start date must be after the start of the event',
            maxDate: 'Start date should be before the end of the event or workshop',
          }"
          :validator="vs.form.from"
          label="Starts"
        />
      </v-flex>
      <v-flex xs6 md12>
        <datetime-field
          v-model="form.to"
          :min="form.from ? form.from.toString() : event.from.toString()"
          :max="event.to.toString()"
          :error-messages="{
            minDate: 'End date must be after the start of the event or workshop',
            maxDate: 'End date must be before the end of the event',
          }"
          :validator="vs.form.to"
          label="Ends"
        />
      </v-flex>
    </v-layout>
    <v-flex class="pb-16">
      <autocomplete-field
        v-model="form.lecturers"
        :items="suggestedLecturers"
        :on-search-change="populateSuggested"
        :loading="suggestionsLoading"
        :filter="lecturersFilter"
        multiple
        label="Lecturers"
        item-render="name"
        return-object
      >
        <template v-slot:selection="{ item }">
          <v-chip small @click="removeAttendee(item.id)">
            {{ item.fields['First name'] }} {{ item.fields['Last name'] }}
          </v-chip>
        </template>
        <template v-slot:item="{ item }">
          <v-list-tile-content>
            {{ item.fields['First name'] }} {{ item.fields['Last name'] }}
          </v-list-tile-content>
        </template>
        <template v-slot:no-data>
          <span style="margin-left: 10px">Start typing attendee name...</span>
        </template>
      </autocomplete-field>
    </v-flex>
    <v-flex v-if="event.extraModules.mobileApp" class="pb-16">
      <autocomplete-field
        v-model="form.hallId"
        :items="suggestedHalls"
        :loading="suggestionsLoadingHalls"
        :filter="hallsFilter"
        label="Hall"
        item-render="name"
        item-select="id"
      >
        <template v-slot:selection="{ item }">
          <v-chip small>
            {{ item['name'] }}
          </v-chip>
        </template>
        <template v-slot:item="{ item }">
          <v-list-tile-content>{{ item['name'] }}</v-list-tile-content>
        </template>
        <template v-slot:no-data>
          <span>No data</span>
        </template>
      </autocomplete-field>
    </v-flex>
    <v-flex>
      <text-field
        v-model.number="form.attendanceLimit"
        :disabled="form.unlimitedAttendance"
        :validator="vs.form.attendanceLimit"
        label="Attendance limit"
        mask="number"
      />
    </v-flex>
    <v-flex class="pb-16">
      <switch-field
        v-model="form.unlimitedAttendance"
        :validator="vs.form.unlimitedAttendance"
        label="Workshops's attendance is unlimited"
      />
    </v-flex>
    <v-flex class="pb-16">
      <textarea-field v-model.trim="form.description" :validator="vs.form.description" />
    </v-flex>
  </v-layout>
</template>

<script>
import { apolloClient } from '../../apollo'
import { SEARCH_EVENT_ACCREDITATIONS } from '../../graphql/event.graphql'
import { SEARCH_HALLS } from '../../graphql/halls.graphql'

export default {
  name: 'workshop-form',
  props: {
    initialValues: {
      type: Object,
      default: () => ({}),
    },
    event: {
      type: Object,
      default: () => ({}),
    },
    halls: {
      type: Array,
      default: () => [],
    },
    vs: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const { lecturers, hall, ...rest } = this.initialValues

    const initialLecturers = (lecturers || []).map(this.suggestedMapper)

    const form = {
      ...rest,
      lecturers: initialLecturers,
      hallId: hall && hall.id,
    }

    return {
      form,
      suggestedLecturers: initialLecturers,
      suggestedHalls: this.halls,
      lecturersSearch: '',
      suggestionsLoading: false,
      suggestionsLoadingHalls: false,
      requestInProgress: undefined,
    }
  },
  watch: {
    form: {
      handler(val) {
        this.$emit('values', val)
      },
      deep: true,
    },
  },
  methods: {
    suggestedMapper(attendee) {
      return {
        name: `${attendee.fields['First name']} ${attendee.fields['Last name']}`,
        ...attendee,
      }
    },
    lecturersFilter({ category, fields }, input) {
      input = input.toLowerCase()
      const fieldsMatch = Object.values(fields).some(fieldValue =>
        fieldValue.toLowerCase().includes(input),
      )
      const categoryMatch = category ? category.name.toLowerCase().includes(input) : false

      return fieldsMatch || categoryMatch
    },
    async populateSuggested(input) {
      if (!input || !input.trim()) return

      if (this.requestInProgress) clearTimeout(this.requestInProgress)
      this.suggestionsLoading = true
      this.requestInProgress = setTimeout(async () => {
        const { data, errors, loading } = await apolloClient.query({
          query: SEARCH_EVENT_ACCREDITATIONS,
          variables: {
            eventId: this.event.id,
            status: 'ALLOWED',
            input,
          },
        })
        if (!errors && !loading) {
          const { searchEventAccreditations } = data
          this.suggestedLecturers = searchEventAccreditations.map(this.suggestedMapper)
        }
        this.suggestionsLoading = false
      }, 500)
    },
    hallsFilter({ name }, input) {
      input = input.toLowerCase()
      const nameMatch = Object.values(name).some(fieldValue =>
        fieldValue.toLowerCase().includes(input),
      )

      return nameMatch
    },
    removeAttendee(id) {
      console.log('Clicked!')
      this.form.lecturers = this.form.lecturers.filter(l => l.id !== id)
    },
  },
}
</script>

<style scoped></style>
